import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Search,
  MapPin,
  Star,
  Sparkles,
  Coffee,
  Hotel,
  UtensilsCrossed,
  Wine,
  Compass,
  Wrench,
  Heart,
  Music2,
  CarFront,
  Store,
  Loader2,
  CheckCircle2,
  Crown,
  Tag,
  Map,
  Ticket,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "todos", label: "Todos", Icon: Sparkles },
  { key: "restaurantes", label: "Restaurantes", Icon: UtensilsCrossed },
  { key: "hoteles", label: "Hoteles", Icon: Hotel },
  { key: "cafes", label: "Cafés", Icon: Coffee },
  { key: "bares", label: "Bares", Icon: Wine },
  { key: "tours", label: "Tours", Icon: Compass },
  { key: "car_wash", label: "Car Wash", Icon: CarFront },
  { key: "mecanicos", label: "Mecánicos", Icon: Wrench },
  { key: "salud_belleza", label: "Salud y Belleza", Icon: Heart },
  { key: "entretenimiento", label: "Entretenimiento", Icon: Music2 },
  { key: "otros", label: "Otros", Icon: Store },
];

const CITIES = ["Todas", "Tijuana", "Rosarito", "Ensenada", "Mexicali", "San Diego"];

function CategoryLabel({ keyName }) {
  const cat = CATEGORIES.find((c) => c.key === keyName);
  return <span>{cat?.label || keyName}</span>;
}

function BusinessCard({ biz, isFavorite, onToggleFavorite }) {
  const photo = biz.photos?.[0] || biz.logo_url;
  const handleFav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleFavorite?.(biz.business_id);
  };
  return (
    <Link
      to={`/guia/${biz.business_id}`}
      className="card-base hover:shadow-lg transition-all overflow-hidden p-0 flex flex-col group"
      data-testid={`biz-card-${biz.business_id}`}
    >
      <div className="relative aspect-[16/10] bg-neutral-100 overflow-hidden">
        {photo ? (
          <img
            src={photo}
            alt={biz.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-400">
            <Store className="w-12 h-12" />
          </div>
        )}
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {biz.featured && (
            <Badge className="bg-amber-500 text-white border-0 shadow-md gap-1">
              <Crown className="w-3 h-3" />
              Destacado
            </Badge>
          )}
          {biz.verified_partner && (
            <Badge className="bg-emerald-600 text-white border-0 shadow-md gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Verificado
            </Badge>
          )}
        </div>
        {/* Favorite toggle */}
        {onToggleFavorite && (
          <button
            onClick={handleFav}
            className={`absolute top-3 right-12 w-9 h-9 rounded-full bg-white/80 hover:bg-white backdrop-blur flex items-center justify-center transition-all ${
              isFavorite ? "text-rose-500" : "text-neutral-600"
            }`}
            data-testid={`biz-fav-${biz.business_id}`}
            aria-label="Favorito"
          >
            <Heart className={`w-4 h-4 ${isFavorite ? "fill-rose-500" : ""}`} />
          </button>
        )}
        {biz.discount_percent ? (
          <div className="absolute top-3 right-3 bg-terracotta text-white text-base font-bold px-3 py-1 rounded-full shadow-lg">
            -{Math.round(biz.discount_percent)}%
          </div>
        ) : null}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-center gap-2 text-xs text-neutral-500 mb-1">
          <Tag className="w-3 h-3" />
          <CategoryLabel keyName={biz.category} />
        </div>
        <h3 className="font-heading font-semibold text-lg text-agave mb-1 line-clamp-1">
          {biz.name}
        </h3>
        <p className="text-sm text-neutral-600 line-clamp-2 mb-3 flex-1">{biz.description}</p>
        <div className="flex items-center justify-between text-sm text-neutral-700">
          <span className="flex items-center gap-1 truncate">
            <MapPin className="w-3.5 h-3.5 text-terracotta flex-shrink-0" />
            <span className="truncate">{biz.city}</span>
          </span>
          {biz.rating_count > 0 ? (
            <span className="flex items-center gap-1 text-xs">
              <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
              {(biz.rating_avg || 0).toFixed(1)} ({biz.rating_count})
            </span>
          ) : (
            <span className="text-terracotta font-medium hover:underline">Ver →</span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function GuiaPage() {
  const { isAuthenticated, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(searchParams.get("q") || "");

  const activeCategory = searchParams.get("category") || "todos";
  const activeCity = searchParams.get("city") || "Todas";
  const q = searchParams.get("q") || "";

  const updateParam = (key, value) => {
    const sp = new URLSearchParams(searchParams);
    if (!value || value === "todos" || value === "Todas" || value === "") {
      sp.delete(key);
    } else {
      sp.set(key, value);
    }
    setSearchParams(sp, { replace: true });
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (activeCategory !== "todos") params.category = activeCategory;
        if (activeCity !== "Todas") params.city = activeCity;
        if (q) params.q = q;
        const { data } = await api.get("/guia/businesses", { params });
        if (!cancelled) setItems(data.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [activeCategory, activeCity, q]);

  // Load user's favorites set
  const [favorites, setFavorites] = useState(new Set());
  useEffect(() => {
    if (!isAuthenticated) {
      setFavorites(new Set());
      return;
    }
    let cancelled = false;
    api
      .get("/guia/favorites")
      .then(({ data }) => {
        if (!cancelled) setFavorites(new Set((data.items || []).map((b) => b.business_id)));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const toggleFavorite = async (bizId) => {
    if (!isAuthenticated) {
      toast.info("Inicia sesión para guardar favoritos");
      return;
    }
    const wasFav = favorites.has(bizId);
    // Optimistic update
    setFavorites((prev) => {
      const next = new Set(prev);
      if (wasFav) next.delete(bizId);
      else next.add(bizId);
      return next;
    });
    try {
      if (wasFav) await api.delete(`/guia/favorites/${bizId}`);
      else await api.post(`/guia/favorites/${bizId}`);
    } catch {
      // Revert
      setFavorites((prev) => {
        const next = new Set(prev);
        if (wasFav) next.add(bizId);
        else next.delete(bizId);
        return next;
      });
      toast.error("No se pudo actualizar favoritos");
    }
  };

  const showFavoritesOnly = searchParams.get("favorites") === "1";
  const filteredItems = showFavoritesOnly
    ? items.filter((b) => favorites.has(b.business_id))
    : items;

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    updateParam("q", searchInput.trim());
  };

  const featured = useMemo(() => filteredItems.filter((b) => b.featured), [filteredItems]);
  const rest = useMemo(() => filteredItems.filter((b) => !b.featured), [filteredItems]);

  return (
    <div className="min-h-screen bg-sand pb-12" data-testid="guia-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-agave via-agave/95 to-terracotta text-white">
        <div className="container-main py-10 md:py-14">
          <div className="max-w-2xl">
            <h1 className="text-3xl sm:text-5xl font-heading font-bold mb-2">Guía RivoNes</h1>
            <p className="text-lg sm:text-xl text-white/85 mb-6">
              Descubre, viaja y ahorra en cada parada
            </p>

            {/* Membership pill + quick actions */}
            {isAuthenticated && (
              <div className="flex items-center gap-3 flex-wrap mb-4">
                <div
                  className="inline-flex items-center gap-3 bg-white/15 backdrop-blur-sm rounded-2xl px-4 py-2.5"
                  data-testid="guia-membership-pill"
                >
                  <Sparkles className="w-5 h-5 text-amber-300" />
                  <div className="text-sm">
                    <p className="font-semibold leading-tight">Miembro RivoNes</p>
                    <p className="text-white/80 text-xs">Todos los descuentos activados</p>
                  </div>
                  <Badge className="bg-emerald-500 text-white border-0">Activo</Badge>
                </div>
                <Link
                  to="/guia/mis-cupones"
                  className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-xl px-3 py-2 text-sm font-medium transition-colors"
                  data-testid="guia-mis-cupones-link"
                >
                  <Ticket className="w-4 h-4" />
                  Mis cupones
                </Link>
              </div>
            )}

            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Link
                to="/guia/mapa"
                className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 rounded-xl px-3 py-1.5 text-sm transition-colors"
                data-testid="guia-map-link"
              >
                <Map className="w-4 h-4" />
                Ver en mapa
              </Link>
              {isAuthenticated && (
                <button
                  onClick={() => updateParam("favorites", showFavoritesOnly ? "" : "1")}
                  className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm transition-colors ${
                    showFavoritesOnly ? "bg-rose-500 text-white" : "bg-white/10 hover:bg-white/20 text-white"
                  }`}
                  data-testid="guia-favorites-toggle"
                >
                  <Heart className={`w-4 h-4 ${showFavoritesOnly ? "fill-white" : ""}`} />
                  {showFavoritesOnly ? `Favoritos (${favorites.size})` : "Solo favoritos"}
                </button>
              )}
            </div>

            {/* Search */}
            <form
              onSubmit={handleSearchSubmit}
              className="flex gap-2 bg-white rounded-2xl p-2 shadow-xl max-w-xl"
              data-testid="guia-search-form"
            >
              <div className="flex-1 flex items-center gap-2 px-2 text-neutral-700">
                <Search className="w-5 h-5 text-neutral-400" />
                <Input
                  type="text"
                  placeholder="Busca por nombre, ciudad o categoría"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="border-0 focus-visible:ring-0 shadow-none px-0 text-base"
                  data-testid="guia-search-input"
                />
              </div>
              <Button
                type="submit"
                className="bg-[#111111] hover:bg-neutral-800 text-white rounded-xl"
                data-testid="guia-search-btn"
              >
                Buscar
              </Button>
            </form>
          </div>
        </div>
      </section>

      <div className="container-main py-6">
        {/* Categories filter — horizontal scroll on mobile */}
        <div className="overflow-x-auto -mx-4 px-4 mb-6 scrollbar-hide" data-testid="guia-category-filter">
          <div className="flex gap-2 min-w-max">
            {CATEGORIES.map(({ key, label, Icon }) => {
              const active = activeCategory === key;
              return (
                <button
                  key={key}
                  onClick={() => updateParam("category", key)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all flex items-center gap-2 ${
                    active
                      ? "bg-[#111111] text-white shadow-md"
                      : "bg-white text-neutral-700 hover:bg-neutral-100 border border-neutral-200"
                  }`}
                  data-testid={`guia-cat-${key}`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* City pills */}
        <div className="flex gap-2 mb-6 overflow-x-auto -mx-4 px-4 scrollbar-hide" data-testid="guia-city-filter">
          {CITIES.map((c) => {
            const active = activeCity === c;
            return (
              <button
                key={c}
                onClick={() => updateParam("city", c)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                  active
                    ? "bg-terracotta text-white"
                    : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                }`}
                data-testid={`guia-city-${c.toLowerCase()}`}
              >
                {c}
              </button>
            );
          })}
        </div>

        {/* CTA card (when user is logged in but isn't a business owner yet) */}
        {isAuthenticated && (
          <div
            className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5 mb-6 flex items-center gap-4 flex-wrap"
            data-testid="guia-register-cta"
          >
            <div className="flex-1 min-w-[200px]">
              <h3 className="font-heading font-semibold text-lg text-agave">¿Tienes un negocio?</h3>
              <p className="text-sm text-neutral-600">
                Regístralo y llega a miles de viajeros con descuentos exclusivos.
              </p>
            </div>
            <Link
              to="/guia/registrar"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="guia-register-cta-btn"
            >
              Registrar negocio
            </Link>
          </div>
        )}

        {/* Featured row */}
        {featured.length > 0 && (
          <section className="mb-8" data-testid="guia-featured-section">
            <h2 className="text-lg font-heading font-semibold text-agave mb-3 flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              Destacados
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {featured.map((b) => (
                <BusinessCard
                  key={b.business_id}
                  biz={b}
                  isFavorite={favorites.has(b.business_id)}
                  onToggleFavorite={toggleFavorite}
                />
              ))}
            </div>
          </section>
        )}

        {/* All results */}
        <section>
          <h2 className="text-lg font-heading font-semibold text-agave mb-3">
            {q ? `Resultados para "${q}"` : "Negocios recomendados"}
          </h2>
          {loading ? (
            <div className="py-20 text-center" data-testid="guia-loading">
              <Loader2 className="w-8 h-8 animate-spin text-terracotta mx-auto" />
            </div>
          ) : rest.length === 0 && featured.length === 0 ? (
            showFavoritesOnly ? (
              <div className="py-16 text-center bg-white rounded-2xl border border-dashed border-neutral-300" data-testid="guia-empty-favorites">
                <Heart className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                <p className="text-neutral-700 font-medium mb-1">Aún no tienes favoritos</p>
                <p className="text-sm text-neutral-500 mb-4">Toca el corazón en cualquier negocio para guardarlo.</p>
                <button
                  onClick={() => updateParam("favorites", "")}
                  className="btn-primary inline-flex"
                >
                  Ver todos los negocios
                </button>
              </div>
            ) : (
              <div className="py-16 text-center bg-white rounded-2xl border border-dashed border-neutral-300" data-testid="guia-empty">
                <Store className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                <p className="text-neutral-700 font-medium mb-1">Aún no hay negocios en esta categoría</p>
                <p className="text-sm text-neutral-500 mb-4">¡Sé el primero en registrar el tuyo!</p>
                <Link to="/guia/registrar" className="btn-primary inline-flex">
                  Registrar negocio
                </Link>
              </div>
            )
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="guia-results-grid">
              {rest.map((b) => (
                <BusinessCard
                  key={b.business_id}
                  biz={b}
                  isFavorite={favorites.has(b.business_id)}
                  onToggleFavorite={toggleFavorite}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
